import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/features/auth/auth-context'
import { supabase } from '@/lib/supabase'

interface MfaStatus {
  mfaEnabled: boolean
  graceStartDate: string | null
  loginCount: number
  isGracePeriodExpired: boolean
  daysRemaining: number
  loginsRemaining: number
  loading: boolean
}

const MAX_GRACE_DAYS = 30
const MAX_GRACE_LOGINS = 5

export function useMfaStatus(): MfaStatus {
  const { user } = useAuth()
  const [mfaEnabled, setMfaEnabled] = useState(false)
  const [graceStartDate, setGraceStartDate] = useState<string | null>(null)
  const [loginCount, setLoginCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchMfaStatus = useCallback(async () => {
    if (!user) {
      setLoading(false)
      return
    }

    try {
      // Check MFA factors from Supabase auth
      const { data: factors } = await supabase.auth.mfa.listFactors()
      const hasVerifiedFactor = factors?.totp?.some((f) => f.status === 'verified') ?? false
      setMfaEnabled(hasVerifiedFactor)

      // Read grace period metadata from user profile
      const metadata = user.user_metadata ?? {}
      const start = (metadata.mfa_grace_start as string) ?? user.created_at
      const logins = (metadata.mfa_login_count as number) ?? 0

      setGraceStartDate(start)
      setLoginCount(logins)
    } catch {
      // Default to safe values if we cannot fetch
      setMfaEnabled(false)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchMfaStatus()
  }, [fetchMfaStatus])

  const daysRemaining = calculateDaysRemaining(graceStartDate, MAX_GRACE_DAYS)
  const loginsRemaining = Math.max(0, MAX_GRACE_LOGINS - loginCount)
  const isGracePeriodExpired = !mfaEnabled && (daysRemaining <= 0 || loginsRemaining <= 0)

  return {
    mfaEnabled,
    graceStartDate,
    loginCount,
    isGracePeriodExpired,
    daysRemaining,
    loginsRemaining,
    loading,
  }
}

function calculateDaysRemaining(graceStartDate: string | null, maxDays: number): number {
  if (!graceStartDate) return maxDays
  const start = new Date(graceStartDate)
  const now = new Date()
  const elapsed = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
  return Math.max(0, maxDays - elapsed)
}
